import React, { useEffect, useState } from 'react'
import useTranslation from '../../shared/TranslationProvider'
import Walkthrough from '../../shared/Walkthrough'
import { useJobSearch } from './JobSearchProvider'
import { ACTIONS, EVENTS } from 'react-joyride'
import { useWalkthrough } from './WalkthroughProvider'
import { useWalkthroughFeedbackForm } from '../../Walkthrough/useWalkthroughFeedbackForm'
import { Button } from '../../../lib/ui/button'
import { ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react'
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
} from '../../../lib/ui/dialog'
import { useIsMobile } from '~/hooks/useMediaQuery'

export const WalkthroughStep = ({ stepName }) => {
  const { t } = useTranslation('Walkthrough')
  const { platformName } = useWalkthrough()
  const text = t(`${stepName}.text`, { platform_name: platformName })
  return (
    <div>
      {text.map((t, i) => (
        <p key={`walkthrough-step-${stepName}-${i}`}>{t}</p>
      ))}
    </div>
  )
}

const FeedbackStep = () => {
  const { showFeedbackDialog, toggleShowFeedbackDialog } = useJobSearch()
  const { t } = useTranslation('Walkthrough')
  const yes = t('feedback.yes')
  const no = t('feedback.no')
  const submit = t('feedback.submit')

  const { form, onSubmit, submitted } = useWalkthroughFeedbackForm({
    url: '/job_search_walkthrough_feedbacks',
    toggleShowFeedbackDialog,
  })
  const [selectedFeedback, setSelectedFeedback] = useState(null)

  const submitButtonDisabled = !selectedFeedback || submitted

  const handleYesClick = () => {
    setSelectedFeedback('yes')
    form.setValue('feedback', 'yes')
  }

  const handleNoClick = () => {
    setSelectedFeedback('no')
    form.setValue('feedback', 'no')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    form.handleSubmit(onSubmit)()
  }

  return (
    <Dialog open={showFeedbackDialog} onOpenChange={toggleShowFeedbackDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('feedback.title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-8 p-2">
          <div>
            <div>
              {t('feedback.text').map((t, i) => (
                <p key={`walkthrough-feedback-${i}`}>{t}</p>
              ))}
            </div>
            <div className="flex justify-center gap-2">
              <Button
                type="button"
                variant={selectedFeedback === 'yes' ? 'default' : 'outline'}
                onClick={handleYesClick}
              >
                <ThumbsUpIcon title={yes} />
              </Button>
              <Button
                type="button"
                variant={selectedFeedback === 'no' ? 'default' : 'outline'}
                onClick={handleNoClick}
              >
                <ThumbsDownIcon title={no} />
              </Button>
            </div>
          </div>
          <Button
            disabled={submitButtonDisabled}
            onClick={handleSubmit}
            type="submit"
          >
            {submit}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const JobSearchWalkthrough = () => {
  const { testing, runWalkthrough, form } = useJobSearch()
  const isMobile = useIsMobile()
  const { t } = useTranslation('Walkthrough')

  const mobileSteps = [
    {
      content: <WalkthroughStep stepName="keyword" />,
      target: '.step1',
      spotlightPadding: 15,
      placement: 'right',
      disableBeacon: true,
      title: t('keyword.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="company" />,
      target: '.company_step',
      spotlightPadding: 15,
      placement: 'right',
      disableBeacon: true,
      title: t('company.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="location" />,
      target: '.step2',
      spotlightPadding: 15,
      placement: 'top',
      title: t('location.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="filters" />,
      target: '.step3',
      spotlightPadding: 20,
      placement: 'left',
      title: t('filters.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="search_button" />,
      target: '.step4',
      spotlightPadding: 15,
      placement: 'right',
      title: t('search_button.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="jobs_list" />,
      target: '.step5',
      spotlightPadding: 15,
      placement: 'auto',
      title: t('jobs_list.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="help_center_mobile" />,
      target: '#expand_mobile_menu',
      spotlightPadding: 15,
      placement: 'auto',
      title: t('help_center_mobile.title'),
      spotlightClicks: true,
    },
  ]
  const walkthroughSteps = [
    {
      content: <WalkthroughStep stepName="keyword" />,
      target: '.step1',
      spotlightPadding: 15,
      placement: 'right',
      disableBeacon: true,
      title: t('keyword.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="company" />,
      target: '.company_step',
      spotlightPadding: 15,
      placement: 'right',
      disableBeacon: true,
      title: t('company.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="location" />,
      target: '.step2',
      spotlightPadding: 15,
      placement: 'top',
      title: t('location.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="filters" />,
      target: '.step3',
      spotlightPadding: 20,
      placement: 'left',
      title: t('filters.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="search_button" />,
      target: '.step4',
      spotlightPadding: 15,
      placement: 'right',
      title: t('search_button.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="jobs_list" />,
      target: '.step5',
      spotlightPadding: 15,
      placement: 'right-start',
      title: t('jobs_list.title'),
      spotlightClicks: true,
      disableScrolling: true,
    },
    {
      content: <WalkthroughStep stepName="job_details" />,
      target: '.step6',
      spotlightPadding: 15,
      placement: 'left-start',
      title: t('job_details.title'),
      spotlightClicks: true,
      disableScrolling: true,
    },
    {
      content: <WalkthroughStep stepName="apply_now" />,
      target: '.step6a',
      spotlightPadding: 15,
      placement: 'auto',
      title: t('apply_now.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="see_connections" />,
      target: '.step6b',
      spotlightPadding: 15,
      placement: 'auto',
      title: t('see_connections.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="save_job" />,
      target: '.step6c',
      spotlightPadding: 15,
      placement: 'auto',
      title: t('save_job.title'),
      spotlightClicks: true,
    },
    {
      content: <WalkthroughStep stepName="help_center" />,
      target: isMobile ? '#expand_mobile_menu' : '#help-center',
      spotlightPadding: 15,
      placement: 'auto',
      title: t('help_center.title'),
      spotlightClicks: true,
    },
  ]

  const { toggleShowFeedbackDialog, fakeQuickSearch } = useJobSearch()

  const {
    setState,
    state: { run, stepIndex, steps },
  } = useWalkthrough()

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      run: runWalkthrough || testing,
      tourActive: true,
      stepIndex: 0,
      steps: isMobile ? mobileSteps : walkthroughSteps,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, runWalkthrough, testing, setState])
  const handleJoyrideCallback = (data) => {
    const { action, index, type } = data

    if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
      clearFormFields()
      setState((prev) => ({
        ...prev,
        run: false,
        tourActive: false,
        started: false,
      }))
    }

    if (type === EVENTS.TOUR_END && action !== ACTIONS.SKIP) {
      toggleShowFeedbackDialog()
      clearFormFields()
      setState((prev) => ({
        ...prev,
        run: false,
        tourActive: false,
        started: false,
      }))
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      if (action == ACTIONS.NEXT) {
        setState((prev) => ({ ...prev, stepIndex: index + 1 }))
      } else if (action == ACTIONS.PREV) {
        setState((prev) => ({ ...prev, stepIndex: index - 1 }))
      }
    }

    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setState((prev) => ({
          ...prev,
          stepIndex: index + 1,
          openAllFilters: false,
        }))
      } else if (action == ACTIONS.PREV) {
        setState((prev) => ({
          ...prev,
          stepIndex: index - 1,
          openAllFilters: false,
        }))
      }
    } else if (type === EVENTS.TOOLTIP) {
      if (index === 0) setState((prev) => ({ ...prev, started: true }))
      if (index === 4) {
        fakeQuickSearch()
      }
    }
  }

  const clearFormFields = () => {
    form.reset({
      ...form.getValues(),
      what: '',
      where: '',
      company_name: '',
    })

    // Clear typeahead values for step1, company_step, and step2
    const typeaheadClasses = ['.step1', '.company_step', '.step2']
    typeaheadClasses.forEach((cls) => {
      const el = document.querySelector(cls)
      if (el && window.$) {
        const $el = window.$(el)
        if ($el.typeahead) {
          $el.typeahead('val', '')
        }
      }
    })
  }

  return (
    <>
      <Walkthrough
        locale={t('WalkthroughActions')}
        // the locale prop here passes custom translations to Joyride
        // this auto-maps button titles like "next", "back", "close", and "skip" etc.
        run={run}
        steps={steps}
        callback={handleJoyrideCallback}
        stepIndex={stepIndex}
      />
      <FeedbackStep />
    </>
  )
}

export default JobSearchWalkthrough
